import React, { useState, useCallback, useRef, useEffect } from 'react';
import { affirmations, initialAffirmation } from './constants/affirmations';
import { audioFiles } from './constants/audioFiles';
import './App.css';
import ShareButton from './components/ShareButton';

function App() {
  // Feature flag for simple landing page mode
  const SIMPLE_MODE = process.env.REACT_APP_SIMPLE_MODE === 'true';

  const [affirmation, setAffirmation] = useState(initialAffirmation);
  const [visibleWords, setVisibleWords] = useState(initialAffirmation.split(' ').length);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isButtonPressed, setIsButtonPressed] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const newAffirmationRef = useRef('');
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const oceanSourceRef = useRef(null);
  const oceanBufferRef = useRef(null);
  const oceanGainRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isServerReady, setIsServerReady] = useState(false);

  const playOceanLoop = useCallback(() => {
    if (!oceanBufferRef.current || !audioContextRef.current) {
      console.error('Ocean audio buffer not loaded yet');
      return;
    }

    // Stop existing source if playing
    if (oceanSourceRef.current) {
      try {
        oceanSourceRef.current.stop();
      } catch (e) {
        // Source may already be stopped
      }
    }

    // Create new source for gapless looping
    oceanSourceRef.current = audioContextRef.current.createBufferSource();
    oceanSourceRef.current.buffer = oceanBufferRef.current;
    oceanSourceRef.current.loop = true; // Enable gapless looping
    oceanSourceRef.current.connect(oceanGainRef.current);
    oceanSourceRef.current.start(0);

    console.log('Ocean audio started with gapless looping');
  }, []);

  useEffect(() => {
    if (SIMPLE_MODE) {
      // In simple mode, skip audio initialization
      // Audio will be available in full interactive mode
      return;
    } else {
      // Full mode - initialize both voice and ocean audio
      // Create voice audio element (HTML5 Audio)
      audioRef.current = new Audio();
      audioRef.current.src = audioFiles[0];

      // Initialize Web Audio API for gapless ocean looping
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();

      // Create gain node for volume control
      oceanGainRef.current = audioContextRef.current.createGain();
      oceanGainRef.current.gain.value = 0.20; // 20% volume - subtle background
      oceanGainRef.current.connect(audioContextRef.current.destination);

      // Load ocean audio buffer
      fetch('/audio/innertap_ocean.m4a')
        .then(response => response.arrayBuffer())
        .then(arrayBuffer => audioContextRef.current.decodeAudioData(arrayBuffer))
        .then(audioBuffer => {
          oceanBufferRef.current = audioBuffer;
          console.log('Ocean audio buffer loaded for gapless looping');
        })
        .catch(error => console.error('Ocean audio load error:', error));

      // Log for debugging
      console.log('Voice audio initialized with:', audioRef.current.src);

      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        if (oceanSourceRef.current) {
          oceanSourceRef.current.stop();
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
      };
    }
  }, [SIMPLE_MODE, playOceanLoop]);

  const stopOceanLoop = useCallback(() => {
    if (oceanSourceRef.current) {
      oceanSourceRef.current.loop = false;
      console.log('Ocean audio loop disabled (will finish current loop)');
    }
  }, []);

  const handlePressStart = useCallback(() => {
    setIsButtonPressed(true);
    // Optional haptic feedback for mobile
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
  }, []);

  const handlePressEnd = useCallback(() => {
    setIsButtonPressed(false);
  }, []);

  const handleButtonPress = useCallback(() => {
    // Store current index before increment
    const currentPromptIndex = currentIndex;

    // Always trigger press animation on click
    setIsButtonPressed(true);

    // Release animation after 150ms (matching the animation duration)
    setTimeout(() => {
      setIsButtonPressed(false);
    }, 150);

    // FIRST TAP: Start ocean loop
    if (currentPromptIndex === 0) {
      playOceanLoop();
    }

    setIsFadingOut(true);
    newAffirmationRef.current = affirmations[currentIndex];
    setVisibleWords(0);

    // Optional haptic feedback for mobile
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }

    // Update audio source and play
    if (audioRef.current) {
      // Stop current audio if playing
      audioRef.current.pause();

      // Set new audio source
      audioRef.current.src = audioFiles[currentIndex];
      audioRef.current.currentTime = 0;

      console.log('Playing audio track:', currentIndex + 1, audioFiles[currentIndex]);

      const playPromise = audioRef.current.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('Audio playing successfully');
          })
          .catch(error => {
            console.error('Audio play error:', error);
          });
      }
    }

    // LAST TAP: Disable loop (but let it finish playing)
    if (currentPromptIndex === 10) { // Index 10 = 11th prompt
      stopOceanLoop();
    }

    // Increment index for next tap (loop back to 0 after 10)
    setCurrentIndex((prevIndex) => (prevIndex + 1) % affirmations.length);
  }, [currentIndex, playOceanLoop, stopOceanLoop]);


  useEffect(() => {
    if (isFadingOut) {
      const fadeOutTimer = setTimeout(() => {
        setAffirmation(newAffirmationRef.current);
        setIsFadingOut(false);
        
        let wordIndex = 0;
        const revealInterval = setInterval(() => {
          if (wordIndex < newAffirmationRef.current.split(' ').length) {
            setVisibleWords(prev => prev + 1);
            wordIndex++;
          } else {
            clearInterval(revealInterval);
          }
        }, 300);
      }, 500);

      return () => clearTimeout(fadeOutTimer);
    }
  }, [isFadingOut]);

  // Add server health check
  useEffect(() => {
    let isComponentMounted = true;
    const checkServer = async () => {
      try {
        const response = await fetch('/.netlify/functions/subscribe', {
          method: 'OPTIONS'
        });
        if (isComponentMounted) {
          setIsServerReady(response.ok);
          if (response.ok && error === 'Connecting to server...') {
            setError('');
          }
        }
      } catch (e) {
        if (isComponentMounted) {
          console.log('Server not ready yet');
          setIsServerReady(false);
          setError('Connecting to server...');
        }
      }
    };

    checkServer();
    const interval = setInterval(checkServer, 5000); // Check every 5 seconds instead of 2

    return () => {
      isComponentMounted = false;
      clearInterval(interval);
    };
  }, [error]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isServerReady) {
      setError('Please wait for server connection...');
      return;
    }
    setIsLoading(true);
    // Don't clear error here, let it persist until success or new error
    
    try {
      console.log('Starting submission process...');
      console.log('Email:', email);
      
      const url = '/.netlify/functions/subscribe';
      console.log('Submitting to:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email }),
        timeout: 8000 // 8 second timeout
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries([...response.headers]));

      let data;
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      try {
        data = JSON.parse(responseText);
        console.log('Parsed response data:', data);
      } catch (jsonError) {
        console.error('Error parsing JSON:', jsonError);
        console.error('Invalid JSON:', responseText);
        throw new Error('Failed to parse server response');
      }

      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }

      // Success case
      setIsSubmitted(true);
      setEmail('');
      setError('');
    } catch (error) {
      console.error('Subscription error:', error);
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        setError('Connection issue: Please check your internet connection and try again.');
      } else if (error.message.includes('timeout')) {
        setError('The server is taking too long to respond. Please try again.');
      } else {
        setError(error.message || 'Failed to subscribe. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-container gradient-bg flex-container flex-column justify-between items-center padding-6">
      {!SIMPLE_MODE && <ShareButton />}
      <div className="logo-container">
        <img
          src={`${window.location.origin}/innertap-logo.png`}
          alt="InnerTap Logo"
          className="logo-image"
          style={{
            width: '200px',
            height: 'auto',
            display: 'block',
            margin: '0 auto'
          }}
          onError={(e) => {
            console.error('Logo failed to load');
            console.log('Attempted path:', e.target.src);
          }}
        />
      </div>

      <div className="flex-container flex-column justify-center items-center">
        {SIMPLE_MODE ? (
          // Simple Coming Soon Mode
          <div className="simple-hero" style={{
            textAlign: 'center',
            maxWidth: '600px',
            padding: '2rem 1rem'
          }}>
            {/* Glass morphism card for Coming Soon */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.06)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '20px',
              padding: 'clamp(2rem, 5vw, 3rem) clamp(1.5rem, 4vw, 2.5rem)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.15)',
              marginBottom: '2rem',
              fontFamily: "'Quicksand', sans-serif"
            }}>
              <h1 style={{
                fontSize: 'clamp(2.5rem, 8vw, 5rem)',
                fontWeight: '300',
                color: 'white',
                textShadow: '0 2px 20px rgba(0,0,0,0.4)',
                marginBottom: '1rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontFamily: "'Quicksand', sans-serif"
              }}>
                Coming Soon
              </h1>
              <p style={{
                fontSize: 'clamp(1.1rem, 2.8vw, 1.5rem)',
                color: 'rgba(255,255,255,0.95)',
                textShadow: '0 2px 15px rgba(0,0,0,0.3)',
                fontWeight: '400',
                lineHeight: '1.6',
                fontFamily: "'Quicksand', sans-serif"
              }}>
                Interactive Meditation App
              </p>
            </div>
          </div>
        ) : (
          // Full Interactive Mode
          <>
            <div className="bg-translucent rounded padding-6 margin-bottom-8 min-height-container flex-container items-center justify-center">
              <p className="text-2xl text-white font-semibold text-center">
                {affirmation.split(' ').map((word, index) => (
                  <span
                    key={index}
                    className="inline-block mx-1"
                    style={{
                      opacity: isFadingOut ? 0 : (index < visibleWords ? 1 : 0),
                      transition: isFadingOut ? 'opacity 0.5s ease-out' : 'opacity 0.3s ease-in',
                    }}
                  >
                    {word}
                  </span>
                ))}
              </p>
            </div>

            <div className="button-container">
              <div
                className={`outer-ring ${isButtonPressed ? 'pressed' : ''}`}
                style={{ backgroundImage: `url(${window.location.origin}/outercircle.png)` }}
              ></div>
              <div
                className={`inner-button ${isButtonPressed ? 'pressed' : ''}`}
                role="button"
                tabIndex="0"
                aria-label="Tap for new affirmation"
                onClick={handleButtonPress}
                onMouseDown={handlePressStart}
                onMouseUp={handlePressEnd}
                onMouseLeave={handlePressEnd}
                onTouchStart={handlePressStart}
                onTouchEnd={handlePressEnd}
                onTouchCancel={handlePressEnd}
                style={{ backgroundImage: `url(${window.location.origin}/tap-button.png)` }}
              ></div>
            </div>

            <div className="text-center mb-8">
              <p className="description-text">
                Meditation and affirmations, simplified. Tap through at your own pace. Sight, sound, and touch work together to help busy minds find focus.
              </p>
            </div>

            {/* App Store Badge - Hero */}
            <div className="app-store-hero">
              <a
                href="https://apps.apple.com/us/app/innertap/id6752519125"
                target="_blank"
                rel="noopener noreferrer"
                className="app-store-link"
              >
                <img
                  src={`${window.location.origin}/image/app-store-badge.svg`}
                  alt="Download on the App Store"
                  className="app-store-badge"
                />
              </a>
            </div>

            {/* Feature Showcase Section */}
            <div className="feature-showcase">
              {/* Card 1 - TAP */}
              <div className="feature-card">
                <p className="feature-card-text">Tap to calm your<br />busy mind</p>
                <img
                  src={`${window.location.origin}/image/feature-tap.webp`}
                  alt="Tap to calm your busy mind"
                  className="feature-card-image"
                />
              </div>

              {/* Card 2 - Mixer + Watch */}
              <div className="feature-card">
                <p className="feature-card-text">Create your perfect soundscape for focus</p>
                <img
                  src={`${window.location.origin}/image/feature-mixer.webp`}
                  alt="Create your perfect soundscape for focus"
                  className="feature-card-image"
                />
              </div>

              {/* Card 3 - Sound Page */}
              <div className="feature-card">
                <p className="feature-card-text">Audio for every mood</p>
                <img
                  src={`${window.location.origin}/image/feature-audio.webp`}
                  alt="Audio for every mood"
                  className="feature-card-image"
                />
              </div>
            </div>

            {/* Description Box */}
            <div className="description-box-container">
              <div className="description-glass-card">
                <p className="description-glass-text">
                  InnerTap combines affirmations, meditation, and sound therapy into a simple, hands-on experience.
                </p>
                <p className="description-glass-text">
                  Tap to move through short, guided prompts and sounds that help you refocus, relax, and feel better one tap at a time.
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* App Store Badge - Above Signup */}
      {!SIMPLE_MODE && (
        <div className="app-store-signup">
          <a
            href="https://apps.apple.com/us/app/innertap/id6752519125"
            target="_blank"
            rel="noopener noreferrer"
            className="app-store-link"
          >
            <img
              src={`${window.location.origin}/image/app-store-badge.svg`}
              alt="Download on the App Store"
              className="app-store-badge"
            />
          </a>
        </div>
      )}

      <div className="email-signup">
        {!isSubmitted ? (
          <>
            <p className="signup-text" style={SIMPLE_MODE ? {
              fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
              color: 'rgba(255,255,255,0.95)',
              textShadow: '0 2px 15px rgba(0,0,0,0.3)',
              marginBottom: '1.5rem',
              fontFamily: "'Quicksand', sans-serif"
            } : {}}>
              {SIMPLE_MODE
                ? 'Be the first to experience a new way to meditate'
                : 'Sign up for exclusive affirmations, early access, and maybe a peaceful surprise.'}
            </p>
            <form onSubmit={handleSubmit} className="signup-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={isLoading || !isServerReady}
                style={SIMPLE_MODE ? {
                  fontFamily: "'Quicksand', sans-serif",
                  backgroundColor: 'rgba(17, 24, 39, 0.5)',
                  borderColor: 'rgba(6, 182, 212, 0.5)',
                  boxShadow: '0 0 15px rgba(6, 182, 212, 0.3)'
                } : {}}
                onFocus={(e) => {
                  if (SIMPLE_MODE) {
                    e.target.style.backgroundColor = 'rgba(17, 24, 39, 0.75)';
                    e.target.style.borderColor = 'rgba(6, 182, 212, 1)';
                    e.target.style.boxShadow = '0 0 30px rgba(6, 182, 212, 0.6)';
                  }
                }}
                onBlur={(e) => {
                  if (SIMPLE_MODE) {
                    e.target.style.backgroundColor = 'rgba(17, 24, 39, 0.5)';
                    e.target.style.borderColor = 'rgba(6, 182, 212, 0.5)';
                    e.target.style.boxShadow = '0 0 15px rgba(6, 182, 212, 0.3)';
                  }
                }}
              />
              <button
                type="submit"
                disabled={isLoading || !isServerReady}
                style={SIMPLE_MODE ? {
                  fontFamily: "'Quicksand', sans-serif",
                  backgroundColor: '#10b981',
                  borderColor: '#10b981',
                  color: 'white',
                  fontWeight: '500'
                } : {}}
                onMouseEnter={(e) => {
                  if (SIMPLE_MODE && !isLoading && isServerReady) {
                    e.target.style.backgroundColor = '#059669';
                  }
                }}
                onMouseLeave={(e) => {
                  if (SIMPLE_MODE) {
                    e.target.style.backgroundColor = '#10b981';
                  }
                }}
                onMouseDown={(e) => {
                  if (SIMPLE_MODE && !isLoading && isServerReady) {
                    e.target.style.backgroundColor = '#047857';
                  }
                }}
                onMouseUp={(e) => {
                  if (SIMPLE_MODE && !isLoading && isServerReady) {
                    e.target.style.backgroundColor = '#059669';
                  }
                }}
              >
                {isLoading ? 'Joining...' : !isServerReady ? 'Connecting...' : (SIMPLE_MODE ? 'Notify Me' : 'I AM Ready')}
              </button>
              {error && (
                <p className="error-text" style={{ color: 'rgba(255, 99, 71, 0.9)', marginTop: '0.5rem' }}>
                  {error}
                </p>
              )}
              {SIMPLE_MODE && (
                <p className="privacy-text" style={{
                  fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                  color: 'rgba(255,255,255,0.85)',
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  marginTop: '1rem',
                  fontFamily: "'Quicksand', sans-serif",
                  fontWeight: '400'
                }}>
                  We guard your email like a meditation master guards inner peace.
                </p>
              )}
              {!SIMPLE_MODE && (
                <p className="privacy-text">
                  We guard your email like a meditation master guards inner peace.
                </p>
              )}
              <p className="privacy-text" style={SIMPLE_MODE ? {
                fontFamily: "'Quicksand', sans-serif"
              } : {}}>
                <a href="/legal">Terms &amp; Privacy</a> · <a href="mailto:support@innertap.com?subject=InnerTap%20Support%20Request&body=Hi%20InnerTap%20Team%2C%0A%0AWe're%20here%20to%20help!%20%F0%9F%92%99%20Whatever%20you're%20experiencing%2C%20just%20know%20that%20support%20is%20on%20the%20way.%0A%0ATake%20a%20breath.%20You're%20taking%20a%20positive%20step%20by%20reaching%20out.%0A%0A%5BDescribe%20your%20question%20or%20issue%20here%5D">Contact</a>
              </p>
            </form>
          </>
        ) : (
          <div className="success-message">
            <p>
              {SIMPLE_MODE
                ? "Success! You'll be among the first to know when InnerTap launches. Get ready to transform your meditation practice."
                : "Success! Today, you planted a seed of intention. Nurture it with us as InnerTap guides you toward greater clarity, purpose, and joy."}
            </p>
            <div className="social-links">
              <p>Connect with us for more daily affirmations</p>
              <div className="social-buttons">
                <a
                  href="https://youtube.com/@innertapapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-button"
                  aria-label="Follow us on YouTube"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
                    <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />
                  </svg>
                </a>
                <a
                  href="https://instagram.com/innertapapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-button"
                  aria-label="Follow us on Instagram"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                  </svg>
                </a>
                <a
                  href="https://www.tiktok.com/@innertapapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-button"
                  aria-label="Follow us on TikTok"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
                  </svg>
                </a>
              </div>
            </div>
            <p className="privacy-text">
              <a href="/legal">Terms & Privacy</a> · <a href="mailto:support@innertap.com?subject=InnerTap%20Support%20Request&body=Hi%20InnerTap%20Team%2C%0A%0AWe're%20here%20to%20help!%20%F0%9F%92%99%20Whatever%20you're%20experiencing%2C%20just%20know%20that%20support%20is%20on%20the%20way.%0A%0ATake%20a%20breath.%20You're%20taking%20a%20positive%20step%20by%20reaching%20out.%0A%0A%5BDescribe%20your%20question%20or%20issue%20here%5D">Contact</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
